import React, { useState, useEffect, useMemo } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import Content from "../../layout/content/Content";
import Head from "../../layout/head/Head";
import {
  Card,
  Row,
  Col,
  Button,
  Spinner,
  Alert,
  Badge,
  Dropdown,
  DropdownToggle,
  DropdownMenu,
  DropdownItem,
} from "reactstrap";
import {
  Block,
  BlockBetween,
  BlockHead,
  BlockHeadContent,
  BlockTitle,
  Icon,
} from "../../components/Component";
import {
  Table,
  Collapse as AntCollapse,
  TableColumnsType,
  Timeline,
  Popover,
  Empty,
  Flex,
  Spin,
  Tag,
  Modal,
  Form,
  Input,
  Upload,
  DatePicker,
  InputNumber,
  message,
  Divider,
} from "antd";
import {
  PaperClipOutlined,
  DownloadOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  UploadOutlined,
  EditOutlined,
  SendOutlined,
  InboxOutlined,
} from "@ant-design/icons";
import { mockWorkOrders } from "../../data/mockWorkOrders";
import dayjs from "dayjs";
import Swal from "sweetalert2";
import { formatCurrency } from "../../utils/Utils";
import workExecutionApis from "../../api/WorkOrderManagement/WorkExecutionApis";
import workOrderApis from "../../api/WorkOrderManagement/WorkOrderApis";

const { Panel } = AntCollapse;
const { TextArea } = Input;
const { RangePicker } = DatePicker;
const { Dragger } = Upload;

interface WorkLine {
  id: string;
  workLineNumber: string;
  workLineTitle: string;
  description: string;
  approvedQuantity: number;
  executedQuantity: number;
  cumulativeExecuted: number;
  percentCompletion: number;
  remainingQuantity: number;
  remarks: string;
  uom: string;
}

const WorkExecutionWorkOrderDetails: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const mode = searchParams.get("mode") || "update"; // 'update', 'approval', 'closed'
  const batchId = searchParams.get("batchId");
  const lineId = searchParams.get("lineId");

  const [workOrder, setWorkOrder] = useState<any>(null);
  const [batch, setBatch] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [screenSize, setScreenSize] = useState(0);
  const [mobileView, setMobileView] = useState(false);
  const [sm, setSm] = useState(false);
  const [shareOptionsToggle, setShareOptionsToggle] = useState(false);
  const [executionModalVisible, setExecutionModalVisible] = useState(false);
  const [actionModalVisible, setActionModalVisible] = useState(false);
  const [actionType, setActionType] = useState<"approve" | "return" | null>(
    null
  );
  const [workLines, setWorkLines] = useState<WorkLine[]>([]);
  const [form] = Form.useForm();
  const [actionForm] = Form.useForm();
  const [fileList, setFileList] = useState<any[]>([]);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (id) {
      loadWorkOrderData(id);
    }
    viewChange();
    window.addEventListener("load", viewChange);
    window.addEventListener("resize", viewChange);
    const headerClick = () => setSm(false);
    document
      .getElementsByClassName("nk-header")[0]
      ?.addEventListener("click", headerClick);

    return () => {
      window.removeEventListener("resize", viewChange);
      window.removeEventListener("load", viewChange);
      document
        .getElementsByClassName("nk-header")[0]
        ?.removeEventListener("click", headerClick);
    };
  }, [id]);

  // Load work lines when workOrder changes
  useEffect(() => {
    if (workOrder) {
      loadWorkLines();
    }
  }, [workOrder, mode]);

  const viewChange = () => {
    setScreenSize(window.innerWidth);
    if (window.innerWidth < 990) {
      setMobileView(true);
    } else {
      setMobileView(false);
      setSm(false);
    }
  };

  const mapApiResponseToComponent = (apiData: any) => {
    return {
      workOrderId: apiData.workOrderId,
      woNumber: apiData.workOrderNumber,
      title: apiData.workOrderTitle,
      description: apiData.description,
      status: apiData.status,
      site: apiData.siteName,
      project: apiData.projectName || 'Not specified',
      organisation: apiData.orgName || 'Not specified',
      contractType: apiData.contractType,
      contractWith: apiData.contractWith,
      woValue: apiData.totalValue,
      grossValue: apiData.grossValue,
      outstandingAmount: apiData.outstandingAmount,
      taxAmount: apiData.taxAmount,
      dateReleased: apiData.releasedAt || apiData.createdAt,
      contractOwner: apiData.workOrderOwnerName,
      contractorName: apiData.contractorName,
      contractorId: apiData.contractorId,
      plannedStartDate: apiData.plannedStartDate,
      plannedEndDate: apiData.plannedEndDate,
      slaAge: apiData.slaAgingForConfirmation || 0,
      progressPercentage: 0, // Calculate based on work lines if needed

      // Tax Information
      enableTax: apiData.enableTax,
      taxLevel: apiData.taxLevel,
      tdsRate: apiData.tdsRate,
      tcsRate: apiData.tcsRate,
      tdsTaxId: apiData.tdsTaxId,
      tdsTaxName: apiData.tdsTaxName,
      tcsTaxId: apiData.tcsTaxId,
      tcsTaxName: apiData.tcsTaxName,
      totalValue: apiData.totalValue,

      // Budget Information
      budgetHeaderId: apiData.budgetHeaderId,
      budgetName: apiData.budgetName,
      workOrderLines: apiData.workOrderLines?.map((line: any) => ({
        workOrderLineId: line.workOrderLineId,
        taskName: line.taskTitle,
        workCategory: line.workCategoryName,
        quantity: line.quantity,
        uom: line.uomName || 'Units',
        rate: line.rate,
        taxId: line.taxId,
        taxName: line.taxName,
        taxAmount: line.taxAmount,
        lineValue: line.lineValue,
        total: line.lineValue,
        description: line.description,
        sequence: line.sequence,
        status: line.status,
        sourceType: line.sourceType,
        weightagePct: line.weightagePct,
        allocatedQty: line.allocatedQty,
        // Budget Information
        budgetLineId: line.budgetLineId,
        budgetLineName: line.budgetLineName,
        budgetAllocation: line.budgetAllocation
      })) || [],
      workOrderGeneralConditions: apiData.workOrderGeneralConditions || [],
      workOrderPaymentConditions: apiData.workOrderPaymentConditions || [],
      workOrderApprovals: apiData.workOrderApprovals || [],
      attachments: apiData.attachments || [],
      contractorChangeRequests: apiData.contractorChangeRequests || [],
      dms: apiData.dms || [],
      generalConditions: apiData.workOrderGeneralConditions?.map((condition: any) => condition.content) || [],
      paymentConditions: {
        mobilizationAdvance: {
          percentage: apiData.workOrderPaymentConditions?.find((pc: any) => pc.conditionType === 'Mobilization')?.percentValue || 0,
          amount: apiData.workOrderPaymentConditions?.find((pc: any) => pc.conditionType === 'Mobilization')?.calculatedAmount || 0
        },
        retentionAmount: {
          percentage: apiData.workOrderPaymentConditions?.find((pc: any) => pc.conditionType === 'Retention')?.percentValue || 0,
          releaseDate: apiData.workOrderPaymentConditions?.find((pc: any) => pc.conditionType === 'Retention')?.releaseDate || null
        },
        onHoldAmount: {
          amount: apiData.workOrderPaymentConditions?.find((pc: any) => pc.conditionType === 'OnHold')?.amountValue || 0,
          releaseDate: apiData.workOrderPaymentConditions?.find((pc: any) => pc.conditionType === 'OnHold')?.releaseDate || null
        },
        payOnCompletion: apiData.workOrderPaymentConditions?.some((pc: any) => pc.conditionType === 'PayOnCompletion') || false
      }
    };
  };

  const loadWorkOrderData = async (workOrderId: string) => {
    try {
      setLoading(true);
      if (!workOrderId) {
        message.error("Work Order ID is required");
        setLoading(false);
        return;
      }
      const response: any = await workOrderApis.getWorkOrderById(workOrderId);

      if (response && response.status && response.data) {
        const mappedData = mapApiResponseToComponent(response.data);
        setWorkOrder(mappedData);
        // loadWorkLines will be called in useEffect when workOrder changes
      } else {
        throw new Error(
          response?.message || "Failed to fetch Work Order details"
        );
      }
    } catch (error) {
      console.error("Error loading work order:", error);
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "Failed to load work order data",
      });
    } finally {
      setLoading(false);
    }
  };

  const mapWorkOrderLinesToExecutionLines = (workOrderLines: any[]): WorkLine[] => {
    if (!workOrderLines || workOrderLines.length === 0) {
      return [];
    }

    return workOrderLines.map((line, index) => {
      // Calculate cumulative executed and remaining based on mode and allocatedQty
      const approvedQty = line.quantity || 0;
      const allocatedQty = line.allocatedQty || 0;
      
      let executedQuantity = 0;
      let cumulativeExecuted = allocatedQty;
      let remarks = "";

      // Set different values based on mode
      if (mode === "approval") {
        // For approval mode - show submitted values (simulated)
        executedQuantity = Math.round(approvedQty * 0.1); // 10% of approved as submitted
        remarks = "Submitted for approval";
      } else if (mode === "closed") {
        // For closed mode - show final approved values
        executedQuantity = Math.round(approvedQty * 0.08); // 8% as final approved
        cumulativeExecuted = allocatedQty + executedQuantity;
        remarks = "Work completed and approved";
      }
      // For update mode, executedQuantity remains 0

      const totalCumulative = cumulativeExecuted + executedQuantity;
      const percentCompletion = approvedQty > 0 ? Math.round((totalCumulative / approvedQty) * 100 * 100) / 100 : 0;
      const remainingQuantity = Math.max(0, approvedQty - totalCumulative);

      return {
        id: line.workOrderLineId?.toString() || index.toString(),
        workLineNumber: `WOL-${String(line.sequence || index + 1).padStart(3, '0')}`,
        workLineTitle: line.taskTitle || line.taskName || `Task ${index + 1}`,
        description: line.description || 'No description available',
        approvedQuantity: approvedQty,
        executedQuantity: executedQuantity,
        cumulativeExecuted: cumulativeExecuted,
        percentCompletion: percentCompletion,
        remainingQuantity: remainingQuantity,
        remarks: remarks,
        uom: line.uomName || line.uom || 'Units',
      };
    });
  };

  const loadWorkLines = () => {
    if (!workOrder?.workOrderLines) {
      setWorkLines([]);
      return;
    }

    const executionLines = mapWorkOrderLinesToExecutionLines(workOrder.workOrderLines);
    setWorkLines(executionLines);
  };

  const handleExecutedQuantityChange = (
    value: number | null,
    recordId: string
  ) => {
    if (value === null) return;

    const updatedLines = workLines.map((line) => {
      if (line.id === recordId) {
        const newExecuted = value;
        const newCumulative = line.cumulativeExecuted + newExecuted;
        const newPercent =
          Math.round((newCumulative / line.approvedQuantity) * 100 * 100) / 100;
        const newRemaining = line.approvedQuantity - newCumulative;

        // Validation: cannot exceed approved quantity
        if (newCumulative > line.approvedQuantity) {
          message.error(
            `Cumulative executed quantity cannot exceed approved quantity for ${line.workLineNumber}`
          );
          return line;
        }

        return {
          ...line,
          executedQuantity: newExecuted,
          percentCompletion: newPercent,
          remainingQuantity: Math.max(0, newRemaining),
        };
      }
      return line;
    });

    setWorkLines(updatedLines);
  };

  const handleRemarksChange = (value: string, recordId: string) => {
    const updatedLines = workLines.map((line) => {
      if (line.id === recordId) {
        return { ...line, remarks: value };
      }
      return line;
    });
    setWorkLines(updatedLines);
  };

  const openExecutionModal = () => {
    setExecutionModalVisible(true);
    form.resetFields();
  };

  const openActionModal = (type: "approve" | "return") => {
    setActionType(type);
    setActionModalVisible(true);
    actionForm.resetFields();
  };

  const handleActionSubmit = async (values: any) => {
    try {
      console.log("Submitting action:", actionType, values);

      let message = "";
      let navigationPath = "";

      if (actionType === "approve") {
        message = "Execution batch approved successfully!";
        navigationPath = `${process.env.PUBLIC_URL}/work-execution-pending-approvals`;
      } else if (actionType === "return") {
        message = "Execution batch returned to contractor for revision!";
        navigationPath = `${process.env.PUBLIC_URL}/work-execution-pending-approvals`;
      }

      Swal.fire("Success!", message, "success").then(() => {
        navigate(navigationPath);
      });

      setActionModalVisible(false);
      actionForm.resetFields();
    } catch (error) {
      console.error("Error submitting action:", error);
      Swal.fire("Error!", "Failed to submit action", "error");
    }
  };


  const handleSubmitForApproval = async () => {
    try {
      const values = await form.validateFields();

      // Validate that at least one line has executed quantity > 0
      const hasExecutedWork = workLines.some(
        (line) => line.executedQuantity > 0
      );

      if (!hasExecutedWork) {
        message.error(
          "At least one work line must have executed quantity greater than 0 to submit for approval"
        );
        return;
      }

      setSubmitting(true);

      // Prepare line updates for lines with executed quantity > 0
      const lineUpdates = workLines
        .filter(line => line.executedQuantity > 0)
        .map(line => ({
          workOrderLineId: line.id,
          executedQuantity: line.executedQuantity,
          remarks: line.remarks || "",
        }));

      // Create files data array if files are uploaded
      const filesData = fileList.map((file, index) => ({
        order: index,
        module: "Work Order Management",
        moduleFileType: "Execution Report"
      }));

      // Prepare execution dates from the form
      const executionPeriod = values.executionPeriod;
      const executionStartDate = executionPeriod?.[0] ? executionPeriod[0].format('YYYY-MM-DD') : '';
      const executionEndDate = executionPeriod?.[1] ? executionPeriod[1].format('YYYY-MM-DD') : '';

      const workExecutionData = {
        workOrderId: workOrder.workOrderId,
        executionStartDate: executionStartDate,
        executionEndDate: executionEndDate,
        // updateType: "Progress",
        lineUpdates: lineUpdates,
        submissionComments: values.notes || "Work execution update submitted",
        filesData: filesData
      };

      // Prepare FormData for multipart request
      const formData = new FormData();
      formData.append('workExecution', JSON.stringify(workExecutionData));

      // Add files to form data
      if (fileList.length > 0) {
        fileList.forEach((file, index) => {
          formData.append('files', file.originFileObj || file);
        });
      }

      // Call the API
      const response :any= await workExecutionApis.createWorkExecution(formData);

      if (response && response.status) {
        message.success('Work execution submitted for approval successfully!');
        
        setExecutionModalVisible(false);
        form.resetFields();
        setFileList([]);

        // Reset work lines
        const resetLines = workLines.map((line) => ({
          ...line,
          executedQuantity: 0,
          remarks: "",
        }));
        setWorkLines(resetLines);

        // Navigate back after successful submission
        setTimeout(() => {
          navigate(`${process.env.PUBLIC_URL}/work-execution-under-execution`);
        }, 1500);
      } else {
        throw new Error(response?.message || 'Failed to submit work execution');
      }
    } catch (error) {
      console.error("Error submitting work execution:", error);
      message.error("Failed to submit work execution. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const getStatusColor = (status: string): string => {
    const statusColors: { [key: string]: string } = {
      Pending: "warning",
      Accepted: "success",
      "In Execution": "primary",
      Rejected: "danger",
      "Under Negotiation": "info",
      Finalized: "success",
    };
    return statusColors[status] || "secondary";
  };

  const uploadProps = {
    multiple: true,
    fileList,
    onChange: ({ fileList: newFileList }: any) => setFileList(newFileList),
    beforeUpload: () => false, // Prevent auto upload
    accept: ".pdf,.jpg,.jpeg,.png,.docx",
  };

  // Get column title based on mode
  const getExecutedQtyTitle = () => {
    if (mode === "approval") return "Executed Qty (Submitted)";
    if (mode === "closed") return "Executed Qty (Approved)";
    return "Executed Qty";
  };

  const getCumulativeTitle = () => {
    if (mode === "approval") return "Cumulative (After Approval)";
    if (mode === "closed") return "Cumulative Executed";
    return "Cumulative Executed (Till Date)";
  };

  // Work Lines Table Columns for different modes
  const executionLinesColumns: TableColumnsType<WorkLine> = useMemo(
    () => [
      {
        title: "Work Line #",
        dataIndex: "workLineNumber",
        key: "workLineNumber",
        width: 120,
        fixed: "left",
        render: (text) => <strong>{text}</strong>,
      },
      {
        title: "Work Line Title",
        dataIndex: "workLineTitle",
        key: "workLineTitle",
        width: 180,
        render: (text, record) => (
          <div>
            <div className="fw-bold">{text}</div>
            <div className="text-muted small">{record.description}</div>
          </div>
        ),
      },
      {
        title: "Approved Qty",
        dataIndex: "approvedQuantity",
        key: "approvedQuantity",
        width: 120,
        align: "right",
        render: (qty, record) => (
          <div>
            <div className="fw-bold">{qty.toLocaleString()}</div>
            <div className="text-muted small">{record.uom}</div>
          </div>
        ),
      },
      {
        title: getExecutedQtyTitle(),
        key: "executedQuantity",
        width: 180,
        align: mode === "update" ? "center" : "right",
        render: (_, record) => {
          if (mode === "update") {
            return (
              <InputNumber
                min={0}
                max={record.remainingQuantity + record.executedQuantity}
                precision={2}
                value={record.executedQuantity}
                onChange={(value) =>
                  handleExecutedQuantityChange(value, record.id)
                }
                style={{ width: "100%" }}
                addonAfter={record.uom}
              />
            );
          } else {
            // For approval and closed modes, show as read-only
            const color = mode === "approval" ? "text-primary" : "text-success";
            return (
              <div>
                <div className={`fw-bold ${color}`}>
                  {record.executedQuantity.toLocaleString()}
                </div>
                <div className="text-muted small">{record.uom}</div>
              </div>
            );
          }
        },
      },
      {
        title: getCumulativeTitle(),
        key: "cumulativeExecuted",
        width: 150,
        align: "right",
        render: (_, record) => {
          const cumulative =
            mode === "update"
              ? record.cumulativeExecuted + record.executedQuantity
              : record.cumulativeExecuted;
          return (
            <div>
              <div className="fw-bold text-success">
                {cumulative.toLocaleString()}
              </div>
              <div className="text-muted small">{record.uom}</div>
            </div>
          );
        },
      },
      {
        title: "% of Work Completion",
        key: "percentCompletion",
        width: 140,
        align: "center",
        render: (_, record) => {
          const percent =
            Math.round(
              ((record.cumulativeExecuted + record.executedQuantity) /
                record.approvedQuantity) *
                100 *
                100
            ) / 100;
          const color =
            percent === 100
              ? "#52c41a"
              : percent >= 75
              ? "#faad14"
              : percent >= 50
              ? "#1890ff"
              : "#ff4d4f";
          return (
            <Tag color={color} style={{ minWidth: 60, textAlign: "center" }}>
              {percent}%
            </Tag>
          );
        },
      },
      {
        title: "Remaining Qty",
        key: "remainingQuantity",
        width: 120,
        align: "right",
        render: (_, record) => (
          <div>
            <div className="fw-bold">
              {Math.max(
                0,
                record.approvedQuantity -
                  record.cumulativeExecuted -
                  record.executedQuantity
              ).toLocaleString()}
            </div>
            <div className="text-muted small">{record.uom}</div>
          </div>
        ),
      },
      {
        title: mode === "approval" ? "Contractor Remarks" : "Remarks",
        key: "remarks",
        width: 200,
        render: (_, record) => {
          if (mode === "update") {
            return (
              <TextArea
                rows={2}
                value={record.remarks}
                onChange={(e) => handleRemarksChange(e.target.value, record.id)}
                placeholder="Enter remarks..."
                maxLength={250}
                showCount
              />
            );
          } else {
            // For approval and closed modes, show as read-only
            return record.remarks || <span className="text-muted">---</span>;
          }
        },
      },
    ],
    [mode, workLines]
  );

  // Regular work order lines columns for display
  const workOrderLinesColumns: TableColumnsType<any> = [
    {
      title: "#",
      key: "#",
      width: 50,
      fixed: "left",
      render: (text, record, index) => index + 1,
    },
    {
      title: "Task Name",
      dataIndex: "taskName",
      key: "taskName",
      width: 200,
      fixed: "left",
      render: (text: string) => <div className="fw-bold">{text}</div>,
    },
    {
      title: "Work Category",
      dataIndex: "workCategory",
      key: "workCategory",
      width: 150,
    },
    {
      title: "Budget Details",
      key: "budgetDetails",
      width: 200,
      render: (_, record: any) => (
        <div>
          {record.budgetLineName ? (
            <>
              <div className="fw-bold text-primary">{record.budgetLineName}</div>
              <div className="text-muted small">
                Allocation: {formatCurrency(record.budgetAllocation || 0, "INR")}
              </div>
            </>
          ) : (
            <span className="text-muted">No budget assigned</span>
          )}
        </div>
      ),
    },
    {
      title: "Quantity",
      dataIndex: "quantity",
      key: "quantity",
      width: 100,
      align: "right",
      render: (quantity: number, record: any) =>
        `${quantity?.toLocaleString()} ${record?.uom}`,
    },
    {
      title: "Rate",
      dataIndex: "rate",
      key: "rate",
      width: 120,
      align: "right",
      render: (rate: number) => formatCurrency(rate, "INR"),
    },
    {
      title: "Tax (%)",
      dataIndex: "taxName",
      key: "taxName",
      width: 120,
      align: "center",
      render: (taxName: string) => {
        if (!taxName) return "NA";
        // Extract percentage from taxName like "IGST - 18.00%"
        const percentMatch = taxName.match(/(\d+(?:\.\d+)?)%/);
        if (percentMatch) {
          return `${percentMatch[1]}%`;
        }
        return "NA";
      },
    },
    {
      title: "Tax Amount",
      dataIndex: "taxAmount",
      key: "taxAmount",
      width: 120,
      align: "right",
      render: (taxAmount: number) => (
        <span style={{ color: "#1890ff" }}>
          {formatCurrency(taxAmount || 0, "INR")}
        </span>
      ),
    },
    {
      title: "Subtotal",
      dataIndex: "lineValue",
      key: "lineValue",
      width: 120,
      align: "right",
      render: (lineValue: number) => (
        <strong>{formatCurrency(lineValue || 0, "INR")}</strong>
      ),
    },
    {
      title: "Total (incl. Tax)",
      key: "totalWithTax",
      width: 150,
      align: "right",
      render: (_, record: any) => {
        const subtotal = record.lineValue || record.total || 0;
        const tax = record.taxAmount || 0;
        const total = subtotal + tax;
        return (
          <strong style={{ color: "#52c41a" }}>
            {formatCurrency(total, "INR")}
          </strong>
        );
      },
    },
    {
      title: "Weightage",
      dataIndex: "weightagePct",
      key: "weightagePct",
      width: 100,
      align: "center",
      render: (weightage: number) =>
        weightage != null ? `${weightage}%` : "NA",
    },
  ];

  const getStatusMapper = (status: string) => {
    const statusMap: { [key: string]: { title: string; color: string } } = {
      Draft: { title: "Draft", color: "secondary" },
      Released: { title: "Released", color: "info" },
      Pending: { title: "Pending Review", color: "warning" },
      Accepted: { title: "Accepted", color: "success" },
      "In Execution": { title: "In Execution", color: "primary" },
      Rejected: { title: "Rejected", color: "danger" },
      "Under Negotiation": { title: "Under Negotiation", color: "info" },
      Finalized: { title: "Finalized", color: "success" },
      Active: { title: "Active", color: "success" },
      Completed: { title: "Completed", color: "success" },
      Cancelled: { title: "Cancelled", color: "danger" }
    };
    return statusMap[status] || { title: status, color: "secondary" };
  };

  if (loading) {
    return (
      <>
        <Head title="Work Order Details" />
        <Content>
          <Flex align="center" gap="middle" className="pageLoader">
            <Spin size="large" className="m-auto" />
          </Flex>
        </Content>
      </>
    );
  }

  if (!workOrder) {
    return (
      <>
        <Head title="Work Order Not Found" />
        <Content>
          <div className="text-center py-5">
            <Empty description="Work Order not found" />
          </div>
        </Content>
      </>
    );
  }

  const statusInfo = getStatusMapper(workOrder.status);
  const isSlaBreach = workOrder.slaAge > 2 && workOrder.status === "Pending";

  return (
    <>
      <Head title={`Work Execution - ${workOrder.woNumber}`} />
      <Content>
        <Card className="card-bordered">
          <div className="card-aside-wrap">
            <div className="card-inner card-inner-lg">
              {sm && mobileView && (
                <div
                  className="toggle-overlay"
                  onClick={() => setSm(!sm)}
                ></div>
              )}
              <BlockHead size="lg">
                <BlockBetween>
                  <BlockHeadContent>
                    <BlockTitle tag="h4">
                      <span>
                        Work Execution - {workOrder.woNumber} |{" "}
                        {statusInfo.title}
                      </span>
                      {isSlaBreach && (
                        <Badge color="danger" className="ml-2">
                          SLA Breach
                        </Badge>
                      )}
                    </BlockTitle>
                  </BlockHeadContent>
                  <div className="d-flex">
                    {/* Action buttons based on mode */}
                    {mode === "update" &&
                      (workOrder.status === "Accepted" ||
                        workOrder.status === "In Execution" ||
                        workOrder.status === "Released") && (
                        <div className="actions clearfix">
                          <ul
                            className="m-auto d-flex align-items-center justify-content-center p-0"
                            style={{ flexDirection: "row-reverse" }}
                          >
                            <li className="pt-0 pb-0">
                              <Button
                                color="primary"
                                onClick={openExecutionModal}
                              >
                                <EditOutlined /> Update Work Execution
                              </Button>
                            </li>
                          </ul>
                        </div>
                      )}

                    {mode === "approval" && (
                      <div className="actions clearfix">
                        <ul
                          className="m-auto d-flex align-items-center justify-content-center p-0"
                          style={{ flexDirection: "row-reverse" }}
                        >
                          <li className="pt-0 pb-0">
                            <Button
                              color="success"
                              onClick={() => openActionModal("approve")}
                            >
                              {/* <CheckCircleOutlined />  */}
                              Approve
                            </Button>
                          </li>
                          <li className="pt-0 pb-0">
                            <Button
                              color="warning"
                              onClick={() => openActionModal("return")}
                            >
                              {/* <CloseCircleOutlined /> */}
                              Return
                            </Button>
                          </li>
                        </ul>
                      </div>
                    )}

                    <div className="actions clearfix">
                      <ul
                        className="m-auto d-flex align-items-center justify-content-center p-0"
                        style={{ flexDirection: "row-reverse" }}
                      >
                        <li className="pt-0 pb-0">
                          <Dropdown
                            isOpen={shareOptionsToggle}
                            toggle={() => {
                              setShareOptionsToggle(!shareOptionsToggle);
                            }}
                          >
                            <DropdownToggle className="btn btn-white btn-outline-light">
                              <Icon name="share"></Icon>
                              <span>Share</span>
                            </DropdownToggle>
                            <DropdownMenu>
                              <DropdownItem onClick={() => {}}>
                                <Icon name="mail"></Icon>
                                <span>Send Mail</span>
                              </DropdownItem>
                              <DropdownItem onClick={() => {}}>
                                <Icon name="download"></Icon>
                                <span>Export PDF</span>
                              </DropdownItem>
                            </DropdownMenu>
                          </Dropdown>
                        </li>

                        <li className="pt-0 pb-0">
                          <Button
                            className="toggle"
                            color="primary"
                            onClick={() => {
                              if (mode === "approval") {
                                navigate(
                                  `${process.env.PUBLIC_URL}/work-execution-pending-approvals`
                                );
                              } else if (mode === "closed") {
                                navigate(
                                  `${process.env.PUBLIC_URL}/work-execution-closed-works`
                                );
                              } else {
                                navigate(
                                  `${process.env.PUBLIC_URL}/work-execution-under-execution`
                                );
                              }
                            }}
                          >
                            <Icon name="arrow-left" />
                            <span>Back</span>
                          </Button>
                        </li>
                      </ul>
                    </div>
                  </div>
                  <BlockHeadContent className="align-self-start d-lg-none">
                    <Button
                      className={`toggle btn btn-icon btn-trigger mt-n1 ${
                        sm ? "active" : ""
                      }`}
                      onClick={() => setSm(!sm)}
                    >
                      <Icon name="menu-alt-r" />
                    </Button>
                  </BlockHeadContent>
                </BlockBetween>
              </BlockHead>

              <div className="content clearfix">
                <AntCollapse defaultActiveKey={["1", "2", "3"]}>
                  <Panel
                    header={
                      <div className="d-flex align-items-center justify-content-between">
                        <>General Information</>
                      </div>
                    }
                    key="1"
                  >
                    <Col className="justify-start w-100 flex-wrap mt-3 p-3">
                      <div className="w-30 border-end">
                        <div className="data-col align-center pt-1">
                          <div className="data-label w-30">WO #</div>
                          <div className="data-value w-70">
                            {workOrder.woNumber || "--"}
                          </div>
                        </div>
                        <div className="data-col align-center pt-1">
                          <div className="data-label w-30">Site</div>
                          <div className="data-value w-70">
                            {workOrder.site || "--"}
                          </div>
                        </div>
                        <div className="data-col align-center pt-1">
                          <div className="data-label w-30">Project</div>
                          <div className="data-value w-70">
                            {workOrder.project || "--"}
                          </div>
                        </div>
                        <div className="data-col align-center pt-1">
                          <div className="data-label w-30">Organisation</div>
                          <div className="data-value w-70">
                            <Badge color="outline-primary">
                              {workOrder.organisation || "--"}
                            </Badge>
                          </div>
                        </div>
                      </div>
                      <div className="w-30 border-end paddingLeft10">
                        <div className="data-col align-center pt-1">
                          <div className="data-label w-30">Status</div>
                          <div className="data-value w-70">
                            <Badge color={`outline-${statusInfo.color}`}>
                              {statusInfo.title}
                            </Badge>
                          </div>
                        </div>
                        <div className="data-col align-center pt-1">
                          <div className="data-label w-30">Contract Type</div>
                          <div className="data-value w-70">
                            <Badge color="outline-info">
                              {workOrder.contractType || "--"}
                            </Badge>
                          </div>
                        </div>
                        <div className="data-col align-center pt-1">
                          <div className="data-label w-30">WO Value</div>
                          <div className="data-value w-70">
                            <Badge color="outline-success">
                              {formatCurrency(workOrder.grossValue || workOrder.woValue, "INR")}
                            </Badge>
                            {workOrder.enableTax && (
                              <div className="small text-muted mt-1">
                                Base: {formatCurrency(workOrder.totalValue || workOrder.woValue || 0, "INR")}
                                {workOrder.taxAmount > 0 && (
                                  <span> + Tax: {formatCurrency(workOrder.taxAmount, "INR")}</span>
                                )}
                              </div>
                            )}
                          </div>
                        </div>
                        <div className="data-col align-center pt-1">
                          <div className="data-label w-30">Progress</div>
                          <div className="data-value w-70">
                            <Badge color="outline-info">
                              {workOrder.progressPercentage || 0}%
                            </Badge>
                          </div>
                        </div>
                      </div>
                      <div className="w-30 paddingLeft10">
                        <div className="data-col align-center pt-1">
                          <div className="data-label w-30">Contractor</div>
                          <div className="data-value w-70">
                            {workOrder.contractorName || "--"}
                          </div>
                        </div>
                        <div className="data-col align-center pt-1">
                          <div className="data-label w-30">Title</div>
                          <div className="data-value w-70">
                            <Popover
                              content={
                                <div>
                                  <p>{workOrder.title}</p>
                                </div>
                              }
                              title={"Work Order Title"}
                            >
                              {workOrder.title?.length > 30 ? (
                                <span
                                  style={{
                                    color: "#2263b3",
                                    textDecoration: "underline",
                                    cursor: "pointer",
                                  }}
                                >
                                  {workOrder.title.substring(0, 30)}...
                                </span>
                              ) : (
                                workOrder.title || "--"
                              )}
                            </Popover>
                          </div>
                        </div>
                        <div className="data-col align-center pt-1">
                          <div className="data-label w-30">Released Date</div>
                          <div className="data-value w-70">
                            {dayjs(workOrder.dateReleased).format(
                              "DD-MMM-YYYY"
                            )}
                          </div>
                        </div>
                        <div className="data-col align-center pt-1">
                          <div className="data-label w-30">Contract Owner</div>
                          <div className="data-value w-70">
                            {workOrder.contractOwner || "--"}
                          </div>
                        </div>
                        {workOrder.budgetName && (
                          <div className="data-col align-center pt-1">
                            <div className="data-label w-30">Budget</div>
                            <div className="data-value w-70">
                              <Badge color="outline-info">
                                {workOrder.budgetName}
                              </Badge>
                            </div>
                          </div>
                        )}
                      </div>
                    </Col>

                    {/* Tax Information Section */}
                    {workOrder.enableTax && (
                      <Col className="justify-start w-100 flex-wrap p-3">
                        <Divider>Tax Information</Divider>
                        {workOrder.taxLevel === "Line" ? (
                          // For Line level tax, show in single row centered
                          <div className="w-100 d-flex">
                            <div className="d-flex align-items-center gap-4">
                              <div className="d-flex align-items-center gap-2">
                                <span className="data-label text-nowrap w-60">Tax Enabled:</span>
                                <Badge color="outline-success">
                                  {workOrder.enableTax ? "Yes" : "No"}
                                </Badge>
                              </div>
                              <div className="d-flex align-items-center gap-2">
                                <span className="data-label text-nowrap w-55">Tax Level:</span>
                                <Badge color="outline-info">
                                  {workOrder.taxLevel}
                                </Badge>
                              </div>
                              <div className="text-muted small">
                                <em>(Applied per line)</em>
                              </div>
                            </div>
                          </div>
                        ) : (
                          // For Header/Both level tax, show full 3-column layout
                          <>
                            <div className="w-30 border-end">
                              <div className="data-col align-center pt-1">
                                <div className="data-label w-30">Tax Enabled</div>
                                <div className="data-value w-70">
                                  <Badge color="outline-success">
                                    {workOrder.enableTax ? "Yes" : "No"}
                                  </Badge>
                                </div>
                              </div>
                              <div className="data-col align-center pt-1">
                                <div className="data-label w-30">Tax Level</div>
                                <div className="data-value w-70">
                                  <Badge color="outline-info">
                                    {workOrder.taxLevel || "--"}
                                  </Badge>
                                </div>
                              </div>
                            </div>
                            <div className="w-30 border-end paddingLeft10">
                              <div className="data-col align-center pt-1">
                                <div className="data-label w-30">TDS Rate</div>
                                <div className="data-value w-70">
                                  <Badge color="outline-warning">
                                    {workOrder.tdsRate
                                      ? `${workOrder.tdsRate}%`
                                      : "--"}
                                  </Badge>
                                </div>
                              </div>
                              <div className="data-col align-center pt-1">
                                <div className="data-label w-30">TCS Rate</div>
                                <div className="data-value w-70">
                                  <Badge color="outline-warning">
                                    {workOrder.tcsRate
                                      ? `${workOrder.tcsRate}%`
                                      : "--"}
                                  </Badge>
                                </div>
                              </div>
                            </div>
                            <div className="w-30 paddingLeft10">
                              <div className="data-col align-center pt-1">
                                <div className="data-label w-30">TDS Tax</div>
                                <div className="data-value w-70">
                                  {workOrder.tdsTaxName || "--"}
                                </div>
                              </div>
                              <div className="data-col align-center pt-1">
                                <div className="data-label w-30">TCS Tax</div>
                                <div className="data-value w-70">
                                  {workOrder.tcsTaxName || "--"}
                                </div>
                              </div>
                            </div>
                          </>
                        )}
                      </Col>
                    )}
                  </Panel>

                  <Panel
                    header={`Work Order Lines (${
                      workOrder.workOrderLines?.length || 0
                    })`}
                    key="2"
                  >
                    <div className="table-responsive poTable">
                      <div
                        style={{
                          width: screenSize - 170 + "px",
                          margin: "auto",
                        }}
                      >
                        {!workOrder.workOrderLines ||
                        workOrder.workOrderLines.length === 0 ? (
                          <div className="text-center py-5">
                            <Empty description="No work order lines found" />
                          </div>
                        ) : (
                          <Table
                            className="customTable"
                            columns={workOrderLinesColumns}
                            dataSource={workOrder.workOrderLines}
                            scroll={{ x: 1000 }}
                            bordered
                            size="small"
                            pagination={false}
                            summary={() => {
                              const totalSubtotal = workOrder.workOrderLines.reduce(
                                (sum: number, line: any) => sum + (line.lineValue || line.total || 0),
                                0
                              );
                              const totalTaxAmount = workOrder.workOrderLines.reduce(
                                (sum: number, line: any) => sum + (line.taxAmount || 0),
                                0
                              );
                              const grandTotal = totalSubtotal + totalTaxAmount;

                              return (
                                <Table.Summary>
                                  <Table.Summary.Row>
                                    <Table.Summary.Cell index={0} colSpan={7}>
                                      <strong>Total</strong>
                                    </Table.Summary.Cell>
                                    <Table.Summary.Cell index={1}>
                                      <strong style={{ color: "#1890ff" }}>
                                        {formatCurrency(totalTaxAmount, "INR")}
                                      </strong>
                                    </Table.Summary.Cell>
                                    <Table.Summary.Cell index={2}>
                                      <strong>
                                        {formatCurrency(totalSubtotal, "INR")}
                                      </strong>
                                    </Table.Summary.Cell>
                                    <Table.Summary.Cell index={3}>
                                      <strong style={{ color: "#52c41a" }}>
                                        {formatCurrency(grandTotal, "INR")}
                                      </strong>
                                    </Table.Summary.Cell>
                                    <Table.Summary.Cell index={4}>
                                      {/* Empty cell for Weightage column */}
                                    </Table.Summary.Cell>
                                  </Table.Summary.Row>
                                </Table.Summary>
                              );
                            }}
                          />
                        )}
                      </div>
                    </div>
                  </Panel>

                  <Panel
                    header="Payment Conditions & General Conditions"
                    key="3"
                  >
                    <Row className="mt-3">
                      <Col md={6}>
                        <h6>
                          <strong>Payment Conditions:</strong>
                        </h6>
                        {workOrder.workOrderPaymentConditions && workOrder.workOrderPaymentConditions.length > 0 ? (
                          <div
                            style={{
                              padding: "16px",
                              backgroundColor: "#f8f9fa",
                              borderRadius: "8px",
                              border: "1px solid #e9ecef",
                            }}
                          >
                            {workOrder.workOrderPaymentConditions.map((condition: any, index: number) => (
                              <div
                                key={index}
                                style={{
                                  marginBottom: index < workOrder.workOrderPaymentConditions.length - 1 ? "16px" : "0",
                                  padding: "12px",
                                  backgroundColor: "white",
                                  borderRadius: "6px",
                                  borderLeft: `4px solid ${condition.conditionType === 'Mobilization' ? '#007bff' : 
                                    condition.conditionType === 'Retention' ? '#28a745' : 
                                    condition.conditionType === 'OnHold' ? '#ffc107' : '#dc3545'}`,
                                }}
                              >
                                <div
                                  style={{
                                    display: "flex",
                                    justifyContent: "space-between",
                                    alignItems: "center",
                                  }}
                                >
                                  <span
                                    style={{
                                      fontWeight: "600",
                                      color: "#495057",
                                      fontSize: "14px",
                                    }}
                                  >
                                    {condition.conditionType}:
                                  </span>
                                  <span
                                    style={{
                                      fontWeight: "700",
                                      color: condition.conditionType === 'Mobilization' ? '#007bff' : 
                                        condition.conditionType === 'Retention' ? '#28a745' : 
                                        condition.conditionType === 'OnHold' ? '#e67e22' : '#dc3545',
                                      fontSize: "14px",
                                    }}
                                  >
                                    {condition.isPercent 
                                      ? `${condition.percentValue}%`
                                      : formatCurrency(condition.amountValue, "INR")
                                    }
                                  </span>
                                </div>
                                <div
                                  style={{
                                    marginTop: "4px",
                                    fontSize: "13px",
                                    color: "#6c757d",
                                  }}
                                >
                                  {condition.isPercent && (
                                    <div>Amount: {formatCurrency(condition.calculatedAmount, "INR")}</div>
                                  )}
                                  {condition.interestCharged && (
                                    <div>Interest: {condition.interestRate}%</div>
                                  )}
                                  {condition.releaseDate && (
                                    <div>Release: {dayjs(condition.releaseDate).format("DD-MMM-YYYY")}</div>
                                  )}
                                  {condition.notes && (
                                    <div style={{ fontStyle: 'italic', marginTop: '4px' }}>Notes: {condition.notes}</div>
                                  )}
                                </div>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <div className="text-muted">No payment conditions specified</div>
                        )}
                      </Col>

                      <Col md={6}>
                        <h6>
                          <strong>General Conditions:</strong>
                        </h6>
                        {workOrder.workOrderGeneralConditions && workOrder.workOrderGeneralConditions.length > 0 ? (
                          <div
                            style={{
                              padding: "16px",
                              backgroundColor: "#f8f9fa",
                              borderRadius: "8px",
                              border: "1px solid #e9ecef",
                              maxHeight: "400px",
                              overflowY: "auto",
                            }}
                          >
                            {workOrder.workOrderGeneralConditions.map(
                              (condition: any, index: number) => (
                                <div
                                  key={index}
                                  style={{
                                    marginBottom: "12px",
                                    padding: "12px",
                                    backgroundColor: "white",
                                    borderRadius: "6px",
                                    borderLeft: "4px solid #6c757d",
                                    boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
                                  }}
                                >
                                  <div
                                    style={{
                                      display: "flex",
                                      alignItems: "flex-start",
                                      marginBottom: "8px",
                                    }}
                                  >
                                    <span
                                      style={{
                                        fontWeight: "600",
                                        color: "#007bff",
                                        marginRight: "8px",
                                        fontSize: "13px",
                                      }}
                                    >
                                      {condition.conditionCode}:
                                    </span>
                                    <span
                                      style={{
                                        color: "#495057",
                                        fontSize: "14px",
                                        lineHeight: "1.5",
                                        flex: 1,
                                      }}
                                    >
                                      {condition.content}
                                    </span>
                                  </div>
                                  <div
                                    style={{
                                      fontSize: "12px",
                                      color: "#6c757d",
                                      textAlign: "right",
                                    }}
                                  >
                                    Updated: {dayjs(condition.updatedAt).format("DD-MMM-YYYY")}
                                  </div>
                                </div>
                              )
                            )}
                          </div>
                        ) : (
                          <div
                            style={{
                              padding: "20px",
                              textAlign: "center",
                              color: "#6c757d",
                              fontStyle: "italic",
                              backgroundColor: "#f8f9fa",
                              borderRadius: "8px",
                              border: "1px solid #e9ecef",
                            }}
                          >
                            No general conditions specified
                          </div>
                        )}
                      </Col>
                    </Row>
                  </Panel>
                </AntCollapse>
              </div>
            </div>
          </div>
        </Card>
      </Content>

      {/* Execution Update Modal */}
      <Modal
        title={`Work Execution Update - ${workOrder.woNumber}`}
        open={executionModalVisible}
        onCancel={() => {
          setExecutionModalVisible(false);
          form.resetFields();
          setFileList([]);
        }}
        footer={null}
        width="90%"
        style={{ top: 20 }}
      >
        <Form form={form} layout="vertical">
          {/* Header Information */}
          <Row
            gutter={16}
            style={{
              marginBottom: 16,
              padding: 16,
              background: "#f5f5f5",
              borderRadius: 8,
            }}
          >
            <Col span={6}>
              <div>
                <strong>WO #:</strong> {workOrder.woNumber}
              </div>
            </Col>
            <Col span={6}>
              <div>
                <strong>Contractor:</strong> {workOrder.contractorName}
              </div>
            </Col>
            <Col span={6}>
              <div>
                <strong>Site:</strong> {workOrder.site}
              </div>
            </Col>
            <Col span={6}>
              <div>
                <strong>Total Value:</strong> {formatCurrency(workOrder.woValue, "INR")}
              </div>
            </Col>
          </Row>

          {/* Execution Details */}
          <Row gutter={16} style={{ marginBottom: 16 }}>
            <Col span={8}>
              <Form.Item
                label="Execution Period"
                name="executionPeriod"
                rules={[
                  { required: true, message: "Please select execution period" },
                ]}
              >
                <RangePicker style={{ width: "100%" }} />
              </Form.Item>
            </Col>
            <Col span={16}>
              <Form.Item label="Notes" name="notes">
                <TextArea
                  rows={3}
                  placeholder="Enter additional notes (optional)"
                />
              </Form.Item>
            </Col>
          </Row>

          <Row style={{ marginBottom: 16 }}>
            <Col span={24}>
              <Form.Item label="Attachments" name="attachments">
                <Dragger {...uploadProps} style={{ marginBottom: 16 }}>
                  <p className="ant-upload-drag-icon">
                    <InboxOutlined />
                  </p>
                  <p className="ant-upload-text">
                    Click or drag files to upload
                  </p>
                  <p className="ant-upload-hint">
                    Support for PDF, JPG, PNG, DOCX files. Multiple files
                    allowed.
                  </p>
                </Dragger>
              </Form.Item>
            </Col>
          </Row>

          {/* Work Lines Summary */}
          {workLines.length > 0 && (
            <Row style={{ marginBottom: 16, padding: 12, background: "#e6f7ff", borderRadius: 6 }}>
              <Col span={24}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <strong>Work Lines Summary:</strong>
                  <div>
                    <span>Total Lines: <strong>{workLines.length}</strong></span>
                    <span style={{ marginLeft: 20 }}>
                      Total Approved Qty: <strong>
                        {workLines.reduce((sum, line) => sum + line.approvedQuantity, 0).toLocaleString()}
                      </strong>
                    </span>
                    <span style={{ marginLeft: 20 }}>
                      Remaining Qty: <strong>
                        {workLines.reduce((sum, line) => sum + line.remainingQuantity, 0).toLocaleString()}
                      </strong>
                    </span>
                  </div>
                </div>
              </Col>
            </Row>
          )}

          {/* Work Lines Grid */}
          <div style={{ marginBottom: 16 }}>
            <h6 style={{ marginBottom: 16 }}>
              <strong>Work Lines - Execution Update ({workLines.length} lines)</strong>
            </h6>
            <Table
              columns={executionLinesColumns}
              dataSource={workLines}
              rowKey="id"
              pagination={false}
              scroll={{ x: 1400, y: 400 }}
              size="small"
              bordered
            />
          </div>

          {/* Action Buttons */}
          <div
            style={{
              textAlign: "right",
              borderTop: "1px solid #f0f0f0",
              paddingTop: 16,
            }}
          >
            <Button
              onClick={() => setExecutionModalVisible(false)}
              style={{ marginRight: 8 }}
              disabled={submitting}
            >
              Cancel
            </Button>
            <Button
              // type="primary"
              icon={<SendOutlined />}
              onClick={handleSubmitForApproval}
              loading={submitting}
              disabled={!workLines.some((line) => line.executedQuantity > 0)}
            >
              Submit for Approval
            </Button>
          </div>
        </Form>
      </Modal>

      {/* Action Modal for Approval/Return */}
      <Modal
        title={`${
          actionType === "approve" ? "Approve" : "Return"
        } Execution Batch`}
        open={actionModalVisible}
        onCancel={() => {
          setActionModalVisible(false);
          actionForm.resetFields();
        }}
        footer={null}
        width={600}
      >
        <Form form={actionForm} layout="vertical" onFinish={handleActionSubmit}>
          {actionType === "approve" && (
            <>
              <Form.Item name="approverNotes" label="Approval Notes (Optional)">
                <TextArea
                  rows={3}
                  placeholder="Add any notes about the approval..."
                />
              </Form.Item>
            </>
          )}

          {actionType === "return" && (
            <Form.Item
              name="reason"
              label="Reason for Return"
              rules={[
                {
                  required: true,
                  message: "Please provide reason for returning the batch",
                },
              ]}
            >
              <TextArea
                rows={4}
                placeholder="Please explain why you are returning this execution batch..."
              />
            </Form.Item>
          )}

          <div className="text-right">
            <Button
              className="me-2"
              onClick={() => setActionModalVisible(false)}
            >
              Cancel
            </Button>
            <Button color="primary" type="submit">
              {actionType === "approve" ? "Approve Batch" : "Return Batch"}
            </Button>
          </div>
        </Form>
      </Modal>
    </>
  );
};

export default WorkExecutionWorkOrderDetails;
